/**
 * Safari-safe History API wrapper.
 *
 * Inertia v0.11 persists the entire page object (all props) via
 * history.pushState/replaceState on every navigation and partial reload, and
 * only swaps the page component AFTER that write succeeds (Router.setPage).
 * Safari — alone among the browsers our agents use — throws from those writes:
 *
 *  - QuotaExceededError when the serialized state is too large. Lead and
 *    quotesheet-builder pages carry >1MB of props, so navigating into them
 *    dies mid-visit and the agent is left on the page they came from —
 *    usually the dashboard. Sentry: TOPBROKERCRM-213, TOPBROKERCRM-1DN.
 *  - SecurityError once more than 100 history writes land in 10 seconds.
 *    Every Inertia visit performs at least two writes (scroll save + setPage),
 *    so click-heavy flows like the quotesheet builder trip the limit.
 *    Sentry: TOPBROKERCRM-2K2, TOPBROKERCRM-2K5.
 *
 * This wrapper makes history persistence best-effort instead of fatal:
 *  - pushState: on failure, retry with a tiny marker state so the URL still
 *    commits; if even that fails (rate limit), drop the entry entirely.
 *    Either way the exception no longer propagates, so Inertia goes on to
 *    swap the page component and the navigation visibly happens.
 *  - replaceState: on failure, keep the existing entry untouched. Losing a
 *    scroll-position or partial-reload update is harmless; replacing a
 *    renderable page state with a stub is not.
 *  - An entry holding the marker instead of a real Inertia page cannot be
 *    rendered client-side, so back/forward onto one refetches from the
 *    server: popstate → reload; full page load → clear the marker so
 *    Inertia's isBackForwardVisit() path falls through to the
 *    server-provided initial page.
 *
 * Import this from app.js before createInertiaApp() runs: the popstate
 * interceptor must be registered ahead of Inertia's own handler (listeners
 * fire in registration order), which would otherwise try to render the
 * marker object as a page.
 */

export const STRIPPED_STATE_KEY = '__inertiaHistoryStripped';

export function installSafeHistory(win) {
    const history = win.history;
    const original = {
        pushState: history.pushState.bind(history),
        replaceState: history.replaceState.bind(history),
    };

    history.pushState = function (state, title, url) {
        try {
            return original.pushState(state, title, url);
        } catch (error) {
            try {
                return original.pushState({ [STRIPPED_STATE_KEY]: true }, title, url);
            } catch (retryError) {
                console.warn('[safeHistory] pushState dropped:', retryError.message);
            }
        }
    };

    history.replaceState = function (state, title, url) {
        try {
            return original.replaceState(state, title, url);
        } catch (error) {
            console.warn('[safeHistory] replaceState skipped:', error.message);
        }
    };

    // Full page load onto an entry we previously stripped (e.g. browser back
    // after the tab left memory): drop the marker so Inertia renders the
    // server-provided initial page instead of restoring it from state.
    if (history.state && history.state[STRIPPED_STATE_KEY]) {
        try {
            original.replaceState(null, '', win.location.href);
        } catch (error) {
            // Rate-limited: the popstate interceptor below still covers it.
        }
    }

    win.addEventListener('popstate', (event) => {
        if (event.state && event.state[STRIPPED_STATE_KEY]) {
            event.stopImmediatePropagation();
            win.location.reload();
        }
    });

    return original;
}

if (typeof window !== 'undefined') {
    installSafeHistory(window);
}
